import React, { useState, useEffect, useRef } from 'react';
import { 
  obtenerImagenesGaleria, 
  subirImagenGaleria, 
  eliminarImagenGaleria, 
  actualizarMetadataImagen,
  buscarImagenes,
  obtenerCarpetasGaleria,
  crearCarpetaGaleria,
  actualizarCarpetaGaleria,
  eliminarCarpetaGaleria,
  moverImagenACarpeta
} from '../../data/galeria';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import Modal from './Modal';
import '../../styles/admin/GestionGaleria.css';
import { FaFolder, FaFolderOpen, FaArrowLeft, FaEdit, FaTrashAlt, FaFolderPlus, FaEye, FaExchangeAlt, FaInfo } from 'react-icons/fa';

function GestionGaleria({ onSelectImagen = null, modoSeleccion = false }) {
  const [imagenes, setImagenes] = useState([]);
  const [carpetas, setCarpetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [etiquetas, setEtiquetas] = useState([]);
  const [selectedEtiqueta, setSelectedEtiqueta] = useState('');
  const [modalImage, setModalImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);

  // Estados para navegación y gestión de carpetas
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState('Galería Principal');
  const [folderHistory, setFolderHistory] = useState([]);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [showMoveImageModal, setShowMoveImageModal] = useState(false);
  const [imageToMove, setImageToMove] = useState(null);
  
  const fileInputRef = useRef(null);

  // Cargar imágenes al montar el componente
  useEffect(() => {
    cargarImagenes();
    cargarCarpetas();
  }, []);

  // Limpiar mensaje de éxito después de 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Add useEffect to load images when the currentFolderId changes
  useEffect(() => {
    if (currentFolderId !== undefined) {
      cargarImagenes();
    }
  }, [currentFolderId]);

  // Cargar imágenes desde Firebase
  const cargarImagenes = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Cargando imágenes para carpeta:", currentFolderId);
      const imagenesData = await obtenerImagenesGaleria(currentFolderId);
      console.log(`Cargadas ${imagenesData.length} imágenes`);
      setImagenes(imagenesData);
      
      // Extraer etiquetas únicas para el filtro
      const todasEtiquetas = new Set();
      imagenesData.forEach(img => {
        if (img.metadata?.etiquetas && Array.isArray(img.metadata.etiquetas)) {
          img.metadata.etiquetas.forEach(tag => todasEtiquetas.add(tag));
        }
      });
      setEtiquetas(Array.from(todasEtiquetas).sort());
    } catch (err) {
      console.error("Error al cargar imágenes:", err);
      setError("No se pudieron cargar las imágenes. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Cargar carpetas desde Firebase
  const cargarCarpetas = async () => {
    try {
      const carpetasData = await obtenerCarpetasGaleria();
      setCarpetas(carpetasData);
    } catch (err) {
      console.error("Error al cargar carpetas:", err);
      setError("No se pudieron cargar las carpetas. Por favor, intenta de nuevo.");
    }
  };

  // Navegar a una carpeta
  const navegarACarpeta = (carpeta) => {
    // Si ya estamos en esta carpeta, no hacer nada
    if (currentFolderId === carpeta.id) {
      return;
    }
    
    // Check if we're navigating to a folder that's in our current path history
    const folderIndex = folderHistory.findIndex(f => f.id === carpeta.id);
    
    if (folderIndex !== -1) {
      // If folder is in history, truncate history up to this point
      setFolderHistory(folderHistory.slice(0, folderIndex));
    } else {
      // For top-level folders (siblings), reset the history instead of building a path
      // This makes navigation feel like a flat structure rather than hierarchical
      setFolderHistory([]);
    }
    
    // Establecer la carpeta actual
    setCurrentFolderId(carpeta.id);
    setCurrentFolderName(carpeta.nombre);
    
    // Limpiar búsqueda al cambiar de carpeta
    setSearchTerm('');
    setSelectedEtiqueta('');
    
    // Carga de imágenes ocurrirá en useEffect cuando currentFolderId cambie
  };

  // Navegar hacia atrás (carpeta anterior)
  const navegarAtras = () => {
    if (folderHistory.length === 0) {
      console.log("No hay historial para navegar hacia atrás, volviendo a la galería principal");
      // Si no hay historial pero estamos en una carpeta, volver al nivel principal
      if (currentFolderId !== null) {
        navegarARoot();
      }
      return;
    }
    
    // Obtener la última carpeta del historial
    const ultimaCarpeta = folderHistory[folderHistory.length - 1];
    console.log("Navegando hacia atrás a:", ultimaCarpeta);
    
    // Eliminar la última carpeta del historial primero
    setFolderHistory(folderHistory.slice(0, -1));
    
    // Establecer la carpeta actual - esto disparará el useEffect para cargar imágenes
    setCurrentFolderId(ultimaCarpeta.id);
    setCurrentFolderName(ultimaCarpeta.nombre);
    
    // Limpiar búsqueda al cambiar de carpeta
    setSearchTerm('');
    setSelectedEtiqueta('');
    
    // Ya no es necesario llamar manualmente a cargarImagenes() 
    // porque el useEffect lo hará cuando currentFolderId cambie
  };

  // Navegar al nivel principal
  const navegarARoot = () => {
    // Limpiar historial
    setFolderHistory([]);
    
    // Establecer la carpeta actual como nivel principal
    setCurrentFolderId(null);
    setCurrentFolderName('Galería Principal');
    
    // Limpiar búsqueda
    setSearchTerm('');
    setSelectedEtiqueta('');
    
    // Cargar imágenes del nivel principal
    cargarImagenes();
  };

  // Manejar la selección de archivos
  const handleFileSelect = (e) => {
    if (e.target.files.length === 0) return;
    
    const files = Array.from(e.target.files);
    uploadFiles(files);
  };

  // Subir archivos a Firebase
  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Verificar si es imagen
        if (!file.type.startsWith('image/')) {
          console.warn(`Saltando archivo no soportado: ${file.name}`);
          continue;
        }
        
        // Preparar metadatos iniciales
        const metadata = {
          etiquetas: [],
          descripcion: ''
        };
        
        // Simular progreso (real progress es manejado dentro de subirImagenGaleria)
        setUploadProgress(Math.round((i / files.length) * 100));
        
        // Subir imagen a la carpeta actual
        await subirImagenGaleria(file, metadata, currentFolderId);
      }
      
      setUploadProgress(100);
      setSuccessMessage(`${files.length} imagen(es) subida(s) correctamente`);
      
      // Recargar galería
      await cargarImagenes();
    } catch (err) {
      console.error("Error al subir imágenes:", err);
      setError("No se pudieron subir algunas imágenes. Por favor, intenta de nuevo.");
    } finally {
      setUploading(false);
      // Limpiar input de archivos
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Eliminar una imagen
  const handleDelete = async (imagen) => {
    if (!window.confirm(`¿Estás seguro de eliminar la imagen "${imagen.nombre}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await eliminarImagenGaleria(imagen.id);
      setSuccessMessage("Imagen eliminada correctamente");
      await cargarImagenes();
    } catch (err) {
      console.error("Error al eliminar imagen:", err);
      setError("No se pudo eliminar la imagen. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal para ver/editar imagen
  const openImageModal = (imagen) => {
    setModalImage(imagen);
    setEditingMetadata({
      etiquetas: imagen.metadata?.etiquetas || [],
      descripcion: imagen.metadata?.descripcion || '',
      nombre: imagen.nombre || ''
    });
    setIsModalOpen(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(null);
    setEditingMetadata(null);
  };

  // Guardar cambios en los metadatos
  const handleSaveMetadata = async () => {
    if (!modalImage || !editingMetadata) return;
    
    try {
      // First update metadata in Firestore
      await actualizarMetadataImagen(modalImage.id, editingMetadata);
      
      // If name has changed, update it in the document
      if (editingMetadata.nombre !== modalImage.nombre) {
        // Use updateDoc to update the nombre field
        const imagenDocRef = doc(db, 'galeria', modalImage.id);
        await updateDoc(imagenDocRef, {
          nombre: editingMetadata.nombre
        });
      }
      
      setSuccessMessage("Información de imagen actualizada correctamente");
      
      // Update image in local state
      setImagenes(prev => prev.map(img => 
        img.id === modalImage.id 
          ? { 
              ...img, 
              metadata: editingMetadata,
              nombre: editingMetadata.nombre
            } 
          : img
      ));
      
      closeModal();
    } catch (err) {
      console.error("Error al actualizar información de la imagen:", err);
      setError("No se pudo actualizar la información. Por favor, intenta de nuevo.");
    }
  };

  // Agregar etiqueta a la imagen en edición
  const handleAddTag = (tag) => {
    if (!tag || !editingMetadata) return;
    
    // Verificar que la etiqueta no esté duplicada
    if (!editingMetadata.etiquetas.includes(tag)) {
      setEditingMetadata({
        ...editingMetadata,
        etiquetas: [...editingMetadata.etiquetas, tag]
      });
    }
  };

  // Quitar etiqueta de la imagen en edición
  const handleRemoveTag = (tagToRemove) => {
    if (!editingMetadata) return;
    
    setEditingMetadata({
      ...editingMetadata,
      etiquetas: editingMetadata.etiquetas.filter(tag => tag !== tagToRemove)
    });
  };

  // Actualizar campo de descripción
  const handleDescriptionChange = (e) => {
    if (!editingMetadata) return;
    
    setEditingMetadata({
      ...editingMetadata,
      descripcion: e.target.value
    });
  };

  // Buscar imágenes
  const handleSearch = async () => {
    if (!searchTerm && !selectedEtiqueta) {
      await cargarImagenes();
      return;
    }
    
    setLoading(true);
    try {
      let results;
      
      if (selectedEtiqueta) {
        // Buscar por etiqueta en la carpeta actual
        const allImages = await obtenerImagenesGaleria(currentFolderId);
        results = allImages.filter(img => 
          img.metadata?.etiquetas?.includes(selectedEtiqueta)
        );
        
        // Si también hay término de búsqueda, filtrar más
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          results = results.filter(img => 
            img.nombre.toLowerCase().includes(term) ||
            img.metadata?.descripcion?.toLowerCase().includes(term)
          );
        }
      } else {
        // Buscar solo por término en la carpeta actual
        results = await buscarImagenes(searchTerm, currentFolderId);
      }
      
      setImagenes(results);
    } catch (err) {
      console.error("Error al buscar imágenes:", err);
      setError("Error en la búsqueda. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Limpiar búsqueda
  const handleClearSearch = async () => {
    setSearchTerm('');
    setSelectedEtiqueta('');
    await cargarImagenes();
  };

  // Manejar selección de imágenes (modo selección)
  const handleToggleSelect = (imagen) => {
    if (!modoSeleccion) return;
    
    setSelectedImages(prev => {
      const isSelected = prev.some(img => img.id === imagen.id);
      
      if (isSelected) {
        return prev.filter(img => img.id !== imagen.id);
      } else {
        return [...prev, imagen];
      }
    });
  };

  // Confirmar selección de imágenes
  const handleConfirmSelection = () => {
    if (onSelectImagen && selectedImages.length > 0) {
      onSelectImagen(selectedImages);
    }
  };

  // Seleccionar una única imagen
  const handleSelectSingleImage = (imagen) => {
    if (modoSeleccion && onSelectImagen) {
      onSelectImagen([imagen]);
    }
  };

  // Abrir modal para crear nueva carpeta
  const abrirModalNuevaCarpeta = () => {
    setNewFolderName('');
    setEditingFolderId(null);
    setIsNewFolderModalOpen(true);
  };

  // Abrir modal para editar carpeta
  const abrirModalEditarCarpeta = (carpeta, e) => {
    e.stopPropagation(); // Evitar navegación a la carpeta
    setNewFolderName(carpeta.nombre);
    setEditingFolderId(carpeta.id);
    setIsNewFolderModalOpen(true);
  };

  // Crear o actualizar carpeta
  const guardarCarpeta = async () => {
    if (!newFolderName.trim()) {
      setError("El nombre de la carpeta no puede estar vacío");
      return;
    }

    try {
      if (editingFolderId) {
        // Actualizar carpeta existente
        await actualizarCarpetaGaleria(editingFolderId, newFolderName);
        setSuccessMessage("Carpeta actualizada correctamente");
      } else {
        // Crear nueva carpeta
        await crearCarpetaGaleria(newFolderName);
        setSuccessMessage("Carpeta creada correctamente");
      }
      setIsNewFolderModalOpen(false);
      cargarCarpetas();
    } catch (err) {
      console.error("Error al guardar carpeta:", err);
      setError("No se pudo guardar la carpeta. Por favor, intenta de nuevo.");
    }
  };

  // Eliminar carpeta
  const eliminarCarpeta = async (carpetaId, e) => {
    e.stopPropagation(); // Evitar navegación a la carpeta
    
    const eliminarContenido = window.confirm(
      "¿Deseas eliminar también todas las imágenes de esta carpeta?\n\n" +
      "- Selecciona 'OK' para eliminar la carpeta y todas sus imágenes.\n" +
      "- Selecciona 'Cancelar' para eliminar solo la carpeta y mover las imágenes al nivel principal."
    );
    
    try {
      await eliminarCarpetaGaleria(carpetaId, eliminarContenido);
      setSuccessMessage(
        eliminarContenido
          ? "Carpeta e imágenes eliminadas correctamente"
          : "Carpeta eliminada y las imágenes movidas al nivel principal"
      );
      cargarCarpetas();
      // Si estamos en esa carpeta, volver al nivel principal
      if (currentFolderId === carpetaId) {
        navegarARoot();
      } else {
        // Refrescar las imágenes si estamos en el nivel principal
        if (currentFolderId === null) {
          cargarImagenes();
        }
      }
    } catch (err) {
      console.error("Error al eliminar carpeta:", err);
      setError("No se pudo eliminar la carpeta. Por favor, intenta de nuevo.");
    }
  };

  // Abrir modal para mover imagen
  const abrirModalMoverImagen = (imagen, e) => {
    e.stopPropagation(); // Evitar apertura del modal de detalles
    setImageToMove(imagen);
    setShowMoveImageModal(true);
  };

  // Mover imagen a carpeta
  const moverImagen = async (carpetaId) => {
    if (!imageToMove) return;
    
    try {
      await moverImagenACarpeta(imageToMove.id, carpetaId);
      setSuccessMessage("Imagen movida correctamente");
      setShowMoveImageModal(false);
      setImageToMove(null);
      cargarImagenes(); // Refrescar la vista actual
    } catch (err) {
      console.error("Error al mover imagen:", err);
      setError("No se pudo mover la imagen. Por favor, intenta de nuevo.");
    }
  };

  // Renderizado de la galería
  const renderGaleria = () => {
    if (loading && imagenes.length === 0 && carpetas.length === 0) {
      return <div className="loading-message">Cargando galería...</div>;
    }
    
    const sinContenido = imagenes.length === 0 && carpetas.length === 0;
    
    return (
      <div className="gallery-container">
        {/* Carpetas */}
        {carpetas.length > 0 && (
          <div className="folders-section">
            <h3>Carpetas</h3>
            <div className="folders-grid">
              {carpetas.map(carpeta => (
                <div 
                  key={carpeta.id} 
                  className="folder-card"
                  onClick={() => navegarACarpeta(carpeta)}
                >
                  <div className="folder-icon">
                    <FaFolderOpen />
                  </div>
                  <div className="folder-info">
                    <span className="folder-name">{carpeta.nombre}</span>
                  </div>
                  {!modoSeleccion && (
                    <div className="folder-actions">
                      <button 
                        onClick={(e) => abrirModalEditarCarpeta(carpeta, e)}
                        className="btn-edit-folder"
                        title="Editar carpeta"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={(e) => eliminarCarpeta(carpeta.id, e)}
                        className="btn-delete-folder"
                        title="Eliminar carpeta"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Imágenes */}
        {imagenes.length > 0 ? (
          <div className="images-section">
            <h3>Imágenes</h3>
            <div className="images-grid">
              {imagenes.map(imagen => (
                <div 
                  key={imagen.id} 
                  className={`image-card ${modoSeleccion ? 'selectable' : ''} ${
                    selectedImages.some(img => img.id === imagen.id) ? 'selected' : ''
                  }`}
                  onClick={() => modoSeleccion ? handleToggleSelect(imagen) : null}
                >
                  <div className="image-container">
                    <img src={imagen.url} alt={imagen.nombre} />
                    
                    {/* Botones de acción */}
                    <div className="image-actions">
                      {modoSeleccion ? (
                        <button 
                          className="btn-select" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectSingleImage(imagen);
                          }}
                        >
                          Seleccionar
                        </button>
                      ) : (
                        <>
                          <button 
                            className="btn-view" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageModal(imagen);
                            }}
                            title="Ver detalles"
                          >
                            <FaEye /> <span>Ver</span>
                          </button>
                          <button 
                            className="btn-move" 
                            onClick={(e) => abrirModalMoverImagen(imagen, e)}
                            title="Mover a carpeta"
                          >
                            <FaExchangeAlt /> <span>Mover</span>
                          </button>
                          <button 
                            className="btn-delete" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(imagen);
                            }}
                            title="Eliminar imagen"
                          >
                            <FaTrashAlt /> <span>Borrar</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="image-info">
                    <p className="image-name" title={imagen.nombre}>{imagen.nombre}</p>
                    
                    {imagen.metadata?.etiquetas && imagen.metadata.etiquetas.length > 0 && (
                      <div className="image-tags">
                        {imagen.metadata.etiquetas.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          !sinContenido && !loading && <div className="empty-message">No hay imágenes en esta carpeta</div>
        )}
        
        {sinContenido && !loading && (
          <div className="empty-message">
            {currentFolderId 
              ? "Esta carpeta está vacía. Sube algunas imágenes."
              : "No hay imágenes ni carpetas en la galería."}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="gestion-galeria">
      <h2>Galería de Imágenes</h2>

      {/* Breadcrumbs de navegación */}
      <div className="gallery-breadcrumbs">
        <button 
          className={`btn-breadcrumb ${currentFolderId === null ? 'active' : ''}`}
          onClick={navegarARoot}
        >
          Galería Principal
        </button>
        
        {folderHistory.map((folder, index) => (
          folder.id !== null && (
            <span key={index} className="breadcrumb-separator">
              {' > '}
              <button 
                className="btn-breadcrumb"
                onClick={() => {
                  // Navegar a esta carpeta reconstruyendo el historial
                  setCurrentFolderId(folder.id);
                  setCurrentFolderName(folder.nombre);
                  setFolderHistory(folderHistory.slice(0, index));
                  cargarImagenes();
                }}
              >
                {folder.nombre}
              </button>
            </span>
          )
        ))}
        
        {currentFolderId !== null && (
          <span className="breadcrumb-separator">
            {' > '}
            <span className="current-folder">{currentFolderName}</span>
          </span>
        )}
      </div>

      {/* Mensajes */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {/* Barra de acciones */}
      <div className="gallery-actions">
        {/* Botones de navegación y creación de carpetas (ocultos en modo selección) */}
        {!modoSeleccion && (
          <div className="navigation-actions">
            {currentFolderId !== null && (
              <button 
                className="btn-back" 
                onClick={navegarAtras}
                title="Volver a la carpeta anterior"
              >
                <FaArrowLeft /> Volver
              </button>
            )}
            
            <button 
              className="btn-new-folder" 
              onClick={abrirModalNuevaCarpeta}
              title="Crear nueva carpeta"
            >
              <FaFolderPlus /> Nueva Carpeta
            </button>
          </div>
        )}
        
        {/* Botón de subida (oculto en modo selección) */}
        {!modoSeleccion && (
          <div className="upload-section">
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileSelect} 
              accept="image/*" 
              multiple 
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <button 
              className="btn-upload" 
              onClick={() => fileInputRef.current.click()}
              disabled={uploading}
            >
              {uploading ? `Subiendo... ${uploadProgress}%` : 'Subir Imágenes'}
            </button>
          </div>
        )}
        
        {/* Búsqueda y filtros */}
        <div className="search-section">
          <input 
            type="text" 
            placeholder="Buscar por nombre o descripción..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <select 
            value={selectedEtiqueta}
            onChange={(e) => setSelectedEtiqueta(e.target.value)}
          >
            <option value="">Todas las etiquetas</option>
            {etiquetas.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>
          
          <button className="btn-search" onClick={handleSearch}>Buscar</button>
          <button className="btn-clear" onClick={handleClearSearch}>Limpiar</button>
        </div>
        
        {/* Botones de modo selección (solo en modo selección) */}
        {modoSeleccion && (
          <div className="selection-actions">
            <button 
              className="btn-confirm-selection" 
              onClick={handleConfirmSelection}
              disabled={selectedImages.length === 0}
            >
              Confirmar Selección ({selectedImages.length})
            </button>
          </div>
        )}
      </div>
      
      {/* Galería de imágenes y carpetas */}
      {renderGaleria()}
      
      {/* Modal de detalle de imagen */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Detalles de la imagen"
      >
        {modalImage && (
          <div className="image-detail-modal">
            <div className="image-preview">
              <img src={modalImage.url} alt={modalImage.nombre} />
            </div>
            
            <div className="image-metadata">
              <div className="metadata-section">
                <label>Nombre de la imagen:</label>
                <input 
                  type="text" 
                  value={editingMetadata?.nombre || ''} 
                  onChange={(e) => setEditingMetadata({...editingMetadata, nombre: e.target.value})}
                  placeholder="Nombre de la imagen"
                />
              </div>
              
              <div className="metadata-section">
                <label>Descripción:</label>
                <textarea 
                  value={editingMetadata?.descripcion || ''} 
                  onChange={handleDescriptionChange}
                  placeholder="Agrega una descripción..."
                />
              </div>
              
              <div className="metadata-section">
                <label>Etiquetas:</label>
                
                <div className="tags-editor">
                  <div className="current-tags">
                    {editingMetadata?.etiquetas.map(tag => (
                      <div key={tag} className="tag-item">
                        <span>{tag}</span>
                        <button onClick={() => handleRemoveTag(tag)}>&times;</button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="add-tag">
                    <input 
                      type="text" 
                      placeholder="Nueva etiqueta" 
                      value={selectedEtiqueta}
                      onChange={(e) => setSelectedEtiqueta(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && selectedEtiqueta) {
                          handleAddTag(selectedEtiqueta);
                          setSelectedEtiqueta('');
                        }
                      }}
                    />
                    <button 
                      onClick={() => {
                        handleAddTag(selectedEtiqueta);
                        setSelectedEtiqueta('');
                      }}
                      disabled={!selectedEtiqueta}
                    >
                      Agregar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="image-info-details">
                <p><strong>Tamaño:</strong> {(modalImage.tamaño / 1024).toFixed(2)} KB</p>
                <p><strong>Tipo:</strong> {modalImage.tipo}</p>
                <p><strong>Fecha:</strong> {new Date(modalImage.fechaCreacion.seconds * 1000).toLocaleDateString()}</p>
              </div>
              
              <div className="modal-actions">
                <button className="btn-cancel" onClick={closeModal}>Cancelar</button>
                <button className="btn-save" onClick={handleSaveMetadata}>Guardar Cambios</button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Modal para nueva carpeta / editar carpeta */}
      <Modal
        isOpen={isNewFolderModalOpen}
        onClose={() => setIsNewFolderModalOpen(false)}
        title={editingFolderId ? "Editar Carpeta" : "Nueva Carpeta"}
      >
        <div className="folder-modal">
          <div className="form-group">
            <label>Nombre de la carpeta:</label>
            <input 
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta"
              autoFocus
            />
          </div>
          
          <div className="modal-actions">
            <button 
              className="btn-cancel"
              onClick={() => setIsNewFolderModalOpen(false)}
            >
              Cancelar
            </button>
            <button 
              className="btn-save"
              onClick={guardarCarpeta}
              disabled={!newFolderName.trim()}
            >
              {editingFolderId ? "Actualizar" : "Crear"}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Modal para mover imagen a carpeta */}
      <Modal
        isOpen={showMoveImageModal}
        onClose={() => setShowMoveImageModal(false)}
        title="Mover a Carpeta"
      >
        <div className="move-image-modal">
          <p>Selecciona una carpeta para mover la imagen:</p>
          
          <div className="folder-list">
            <div 
              className="folder-list-item root-folder"
              onClick={() => moverImagen(null)}
            >
              <FaFolder /> Galería Principal
            </div>
            
            {carpetas.map(carpeta => (
              <div 
                key={carpeta.id}
                className={`folder-list-item ${currentFolderId === carpeta.id ? 'current-folder' : ''}`}
                onClick={() => moverImagen(carpeta.id)}
              >
                <FaFolder /> {carpeta.nombre}
                {currentFolderId === carpeta.id && " (Carpeta actual)"}
              </div>
            ))}
          </div>
          
          <div className="modal-actions">
            <button 
              className="btn-cancel"
              onClick={() => setShowMoveImageModal(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default GestionGaleria; 